/**
 * Shared gap-resolution scale for the `Stack` / `Grid` layout primitives.
 *
 * Both components expose a numeric `gap` (`SpaceKey`) that maps onto real
 * `theme.space` tokens. This table is the single source of truth for that
 * mapping — previously it was copy-pasted four times (Stack inline-style
 * map + three Grid recipe variant tables), which let them drift and hid the
 * `9 → space[10]` silent alias. Defining it once here:
 *   - guarantees `Stack` and `Grid` agree key-for-key;
 *   - makes the `SpaceKey` union and this table provably exhaustive
 *     (the `satisfies Record<SpaceKey, string>` below fails to compile if a
 *     key is missing or an extra key creeps in);
 *   - means dropping/adding a gap step is a one-line change.
 *
 * Every key resolves to a *distinct* token — there are no aliases.
 */
import type { SpaceKey } from '../types';
import { theme } from '../tokens';

/**
 * `gap` key → `theme.space` token. Keyed to the numeric `SpaceKey` scale so
 * callers can write the idiomatic `gap={5}` literal. `satisfies` (not a
 * type annotation) keeps the literal key set visible to vanilla-extract's
 * recipe-variant typing while still enforcing exhaustiveness.
 */
export const GAP_SCALE = {
  0: theme.space[0],
  1: theme.space[1],
  2: theme.space[2],
  3: theme.space[3],
  4: theme.space[4],
  5: theme.space[5],
  6: theme.space[6],
  7: theme.space[7],
  8: theme.space[8],
  10: theme.space[10],
  11: theme.space[16],
} as const satisfies Record<SpaceKey, string>;

/** Every `SpaceKey`, in scale order. Single source for table iteration. */
export const SPACE_KEYS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 10, 11] as const satisfies SpaceKey[];

/**
 * Build a vanilla-extract recipe `variants` table that sets a single CSS
 * gap-family property (`gap` / `rowGap` / `columnGap`) to the matching
 * `theme.space` token for every `SpaceKey`. Used by `Grid.css.ts` so its
 * three gap recipes derive from `GAP_SCALE` rather than re-listing it.
 */
export function gapVariants<P extends 'gap' | 'rowGap' | 'columnGap'>(
  property: P,
): Record<SpaceKey, Record<P, string>> {
  const out = {} as Record<SpaceKey, Record<P, string>>;
  for (const key of SPACE_KEYS) {
    out[key] = { [property]: GAP_SCALE[key] } as Record<P, string>;
  }
  return out;
}
